import Stack from "@mui/material/Stack";
import SectionHeader from "../../../../util/component/SectionHeader";
import Box from "@mui/material/Box";
import { CBAuth, CBCreationOfProps, CBCustomerState, CBCustomerStateId, CBOmissionType, CBPartnerType, CustomerBaseDto } from "../../types";
import { useTranslation } from "react-i18next";
import Hidden from "@mui/material/Hidden";
import Typography from "@mui/material/Typography";
import Grid from "@mui/material/Grid";
import CustomerBaseState from "./CustomerBaseState";
import RegistryPhaseDetails from "./RegistryPhaseDetails";
import CBDigitalIdentity from "./CBDigitalIdentity";
import CBDigitalIdentityStatus from "./CBDigitalIdentityStatus";
import CBSubscriptionStatus from "./CBSubscriptionStatus";
import CBCarSharingStatus from "./CBCarSharingStatus";
import { WidthFull } from "@mui/icons-material";
import { Dialog, DialogContent, DialogContentText, DialogTitle, Table, TableBody, TableCell, TableContainer, TableHead, TableRow } from "@mui/material";
import { useEffect, useState } from "react";
import { getCustomerBaseStatusHistoryAsync } from "../../api/service/customerbaseApi";
import AgGridCustomTable from "../../../../util/component/AgGridCustomTable";
import { useHasScope } from "../../../../config/globalConfig";

interface CBSectionHeaderProps extends CBCreationOfProps {
    customer: CustomerBaseDto | null
}

const CBSectionHeader = ({ customer, creationOf }: CBSectionHeaderProps) => {
    const { t } = useTranslation("customerbase");
    const isCreationMode = !!creationOf;
    const isPersonCreation = creationOf == "person";
    const hasScope = useHasScope;

    const [showChangeLogDialog, setShowChangeLogDialog] = useState(false);
    const [cbStatusHistoryData, setCbStatusHistoryData] = useState([]);

    const getField = (translationLabel: string, field: string | null | undefined) => {
        return (
            <Stack direction="row" alignItems="end" columnGap={1}>
                <Typography variant="subtitle2">{t(`${translationLabel}`)}:</Typography>
                <Typography>{!!field ? field : CBOmissionType.OMISSION_DASH}</Typography>
            </Stack>
        );
    }

    useEffect(() => {
        async function updateHistoryData() {
            const data = await getCustomerBaseStatusHistoryAsync(customer?.cbCustomerId);
            setCbStatusHistoryData(data?.data || []);
        }
        if (customer?.cbCustomerId) {
            updateHistoryData();
        }
    }, [customer]);

    const statusMap: Record<number, string> = {
        [CBCustomerStateId.ACTIVE]: CBCustomerState.ACTIVE,
        [CBCustomerStateId.BLOCKED]: CBCustomerState.BLOCKED,
        [CBCustomerStateId.SUSPENDED]: CBCustomerState.SUSPENDED,
        [CBCustomerStateId.WITHDRAWN]: CBCustomerState.WITHDRAWN,
    };

    function getTable() {
        const rows = Array.isArray(cbStatusHistoryData)
            ? cbStatusHistoryData
            : Object.values(cbStatusHistoryData ?? {});

        const columns = [
            {
                headerName: t("creationDate"),
                field: "dateTime",
                valueFormatter: (params: any) =>
                    new Date(params.value).toLocaleString()
            },
            {
                headerName: t("status"),
                field: "status",
                valueFormatter: (params: any) =>
                    t("customerState." + statusMap[params.value]) || params.value,
            },
            {
                headerName: t("reason"),
                field: "reason",
                valueFormatter: (params: any) => {
                    if (statusMap[params.data?.status] === CBCustomerState.ACTIVE) {
                        return "";
                    }
                    return t("customerStateChangeReason." + params.value) || params.value;
                },
            },
            { headerName: t("operator"), field: "assistantId" },
        ];

        return (
            <Box sx={{ width: '100%', height: '100%' }}>
                <AgGridCustomTable
                    columnDefs={columns}
                    rowData={rows}
                    defaultColDef={{
                        sortable: true,
                        filter: true,
                        flex: 1,
                        resizable: true
                    }}
                    pagination={false}
                    domLayout="autoHeight"
                />
            </Box>
        );
    }

    function getChangeLogDialog() {
        return <>
            {showChangeLogDialog &&
                <Dialog
                    open={showChangeLogDialog}
                    onClose={() => setShowChangeLogDialog(false)}
                    maxWidth="md"
                    fullWidth
                >
                    <DialogTitle id="responsive-dialog-title" sx={{ display: 'flex', justifyContent: 'center' }}>
                        {t("customerCareStatusHistory")}
                    </DialogTitle>
                    <DialogContent>
                        {getTable()}
                    </DialogContent>
                </Dialog>
            }
        </>
    }

    return (
        <SectionHeader
            title={
                <Stack direction={{ sm: "row", xs: "column" }} alignItems={{ sm: "center", xs: "flex-start" }} gap={1}>
                    {/*<IconButton color="primary" onClick={() => {
                        if(formState.isDirty)
                            setOpenModal(true);
                        else
                            navigate(-1);
                        }}
                        style={{padding: 0}}
                    >
                        <ArrowBackOutlined />
                    </IconButton>*/}
                    {isCreationMode &&
                        <Box>
                            {isPersonCreation ? t("headers.createCustomerbaseTitle") : t("headers.createOrganizationTitle")}
                        </Box>
                    }
                    {!isCreationMode &&
                        <>
                            {customer?.partnerType ?
                                (customer?.partnerType == CBPartnerType.PERSON ?
                                    (customer.registry?.lastName && customer.registry?.firstName ? (customer.registry?.lastName + ' ' + customer.registry?.firstName) : t("headers.viewCustomerbaseTitle"))
                                    :
                                    (customer.registry?.businessName ? customer.registry?.businessName : t("headers.viewOrganizationTitle"))
                                )
                                :
                                t("headers.viewCustomerbaseTitle")}
                            {/* CR-DRIVALIA-EPCH (LPP-22837): lo storico e' dello stato cliente, quindi sta sulla sua chip */}
                            <CustomerBaseState
                                onShowHistory={hasScope(CBAuth.VIEW_CUSTOMER_STATUS_HISTORY) ? () => setShowChangeLogDialog(true) : undefined}
                            />
                            <CBDigitalIdentity customer={customer} />
                            <CBDigitalIdentityStatus />
                            <CBSubscriptionStatus customer={customer} />
                            <CBCarSharingStatus customer={customer} />
                        </>
                    }
                </Stack>
            }
            subtitle={
                !isCreationMode && (
                    <>
                        <Hidden lgDown>
                            <Stack direction="row" alignItems="center" justifyContent="space-between">
                                <Stack direction="row" alignItems="center" columnGap={3}>
                                    {getField("cbCustomerId", customer?.cbCustomerId)}
                                    {getField("bpCode", customer?.bpCode)}
                                    {getField(customer?.partnerType == CBPartnerType.PERSON ? "taxCode" : "companyFiscalCode", customer?.registry?.taxCode)}
                                    {getField("vatNumber", !!customer?.registry?.vatNumberCountry && !!customer?.registry?.vatNumber ? (customer?.registry?.vatNumberCountry + customer.registry?.vatNumber) : (!!customer?.registry?.vatNumber ? customer?.registry?.vatNumber : ''))}
                                    {(customer?.partnerType == CBPartnerType.PERSON) && getField("drvLicenceNumber", customer?.registry?.drvLicenceNumber)}
                                </Stack>
                                {/*<Stack direction="row" columnGap={2}>
                                {
                                    currentCB?.businessLines?.map((bl, index) =>
                                        <Chip
                                            key={`lgUp-chip-bl-detail-${index}`}
                                            label={t(`sapEnum.businessLines.${bl.description}`)}
                                            color={bl.status == "0" ? "default" : (bl.status == "1" ? "success" : "error") }

                                        />
                                    )
                                }
                            </Stack>*/}
                                <RegistryPhaseDetails />
                            </Stack>
                        </Hidden>
                        <Hidden lgUp>
                            <Grid container alignItems="center">
                                <Grid item lg={0.5} md={4} xs={12}>
                                    {getField("cbCustomerId", customer?.cbCustomerId)}
                                </Grid>
                                <Grid item lg={1} md={4} xs={12}>
                                    {getField("bpCode", customer?.bpCode)}
                                </Grid>
                                <Grid item lg={2} md={4} xs={12}>
                                    {getField(customer?.partnerType == CBPartnerType.PERSON ? "taxCode" : "companyFiscalCode", customer?.registry?.taxCode)}
                                </Grid>
                                <Grid item lg={2} md={4} xs={12}>
                                    {getField("vatNumber", !!customer?.registry?.vatNumberCountry && !!customer?.registry?.vatNumber ? (customer?.registry?.vatNumberCountry + customer.registry?.vatNumber) : (!!customer?.registry?.vatNumber ? customer?.registry?.vatNumber : ''))}
                                </Grid>
                                <Grid item lg={2} md={4} xs={12}>
                                    {(customer?.partnerType == CBPartnerType.PERSON) && getField("drvLicenceNumber", customer?.registry?.drvLicenceNumber)}
                                </Grid>
                                {/*<Grid item lg={12} md={12} xs={12}>
                                <Stack direction="row" columnGap={2} sx={{paddingTop: 1}}>
                                    {
                                        currentCB?.businessLines?.map((bl, index) =>
                                            <Chip
                                                key={`lgDown-chip-bl-detail-${index}`}
                                                label={t(`sapEnum.businessLines.${bl.description}`)}
                                                color={bl.status == "0" ? "default" : (bl.status == "1" ? "success" : "error") }
                                            />
                                        )
                                    }
                                </Stack>
                            </Grid>*/}
                                <RegistryPhaseDetails />
                            </Grid>
                        </Hidden>
                        {hasScope(CBAuth.VIEW_CUSTOMER_STATUS_HISTORY) && getChangeLogDialog()}
                    </>
                )
            }
        />

    );
}

export default CBSectionHeader;